import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Users, Briefcase, Building2, IndianRupee, ListChecks, CreditCard } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AdminLayout, useAdminApi } from '@/components/AdminLayout';

const inr = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

const StatCard = ({ icon: Icon, label, value, accent, testId }) => (
  <Card className="bg-[#0a1a12] border-white/10">
    <CardContent className="pt-6 flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-400">{label}</p>
        <p className="text-3xl font-bold font-mono text-white" data-testid={testId}>{value}</p>
      </div>
      <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: `${accent}22` }}>
        <Icon className="w-5 h-5" style={{ color: accent }} />
      </div>
    </CardContent>
  </Card>
);

export default function AdminDashboardPage() {
  const api = useAdminApi();
  const [m, setM] = useState(null);

  useEffect(() => {
    if (!api) return;
    api.get('/dashboard').then((r) => setM(r.data)).catch(() => setM({}));
  }, [api]);

  if (!m) {
    return <AdminLayout><div className="w-10 h-10 border-4 border-[#EF9F27] border-t-transparent rounded-full animate-spin" /></AdminLayout>;
  }

  const plans = Object.entries(m.users_by_plan || {}).map(([plan, count]) => ({ plan, count }));
  const trials = m.users_by_plan?.trial || 0;

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        <h1 className="text-2xl font-bold font-['Outfit']">Platform overview</h1>
        <p className="text-gray-400">Live metrics across all Laksh portals</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard icon={Users} label="Business users" value={m.total_users || 0} accent="#1D9E75" testId="admin-stat-users" />
        <StatCard icon={Briefcase} label="CA partners" value={m.total_cas || 0} accent="#EF9F27" testId="admin-stat-cas" />
        <StatCard icon={Building2} label="Businesses" value={m.total_businesses || 0} accent="#60a5fa" testId="admin-stat-businesses" />
        <StatCard icon={IndianRupee} label="MRR" value={inr(m.mrr)} accent="#EF9F27" testId="admin-stat-mrr" />
        <StatCard icon={CreditCard} label={`Paid subscriptions · ${trials} on trial`} value={m.active_subscriptions || 0} accent="#1D9E75" testId="admin-stat-subs" />
        <StatCard icon={ListChecks} label="Waitlist" value={m.waitlist_count || 0} accent="#f87171" testId="admin-stat-waitlist" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-[#0a1a12] border-white/10">
          <CardHeader><CardTitle className="text-white text-lg">Signups (last 6 months)</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={m.signups_by_month || []}>
                <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                <YAxis stroke="#6b7280" fontSize={12} allowDecimals={false} />
                <Tooltip cursor={{ fill: '#ffffff0d' }} contentStyle={{ background: '#0a1a12', border: '1px solid #ffffff22' }} />
                <Bar dataKey="count" fill="#1D9E75" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="bg-[#0a1a12] border-white/10">
          <CardHeader><CardTitle className="text-white text-lg">Businesses by plan</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={plans}>
                <XAxis dataKey="plan" stroke="#6b7280" fontSize={12} />
                <YAxis stroke="#6b7280" fontSize={12} allowDecimals={false} />
                <Tooltip cursor={{ fill: '#ffffff0d' }} contentStyle={{ background: '#0a1a12', border: '1px solid #ffffff22' }} />
                <Bar dataKey="count" fill="#EF9F27" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
